import { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary';
import { I18nextProvider } from 'react-i18next';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import App from '@/App';
import Loading from '@/components/loading/Loading';
import ThemeProvider from '@/providers/ThemeProvider';
import { ErrorFallback } from './components/error-fallback';
import './globals.css';
import i18n from './i18n';

const rootElement = document.getElementById('__root__');
const root = createRoot(rootElement);
const queryClient = new QueryClient();

// Handle chunk load errors (cache mismatch after deployments)
// Prevent infinite reload loops by tracking reload attempts
const CHUNK_LOAD_ERROR_KEY = 'chunk_load_error_count';
const MAX_RELOAD_ATTEMPTS = 3;

const handleChunkLoadError = (_errorMessage) => {
  const reloadCount = parseInt(sessionStorage.getItem(CHUNK_LOAD_ERROR_KEY) || '0', 10);

  if (reloadCount < MAX_RELOAD_ATTEMPTS) {
    console.warn(`Chunk load error detected (attempt ${reloadCount + 1}/${MAX_RELOAD_ATTEMPTS}), reloading page...`);
    sessionStorage.setItem(CHUNK_LOAD_ERROR_KEY, String(reloadCount + 1));
    window.location.reload();
  } else {
    console.error('Max reload attempts reached for chunk load error. Please clear browser cache.');
    sessionStorage.removeItem(CHUNK_LOAD_ERROR_KEY);
  }
};

// Reset counter on successful page load
window.addEventListener('load', () => {
  sessionStorage.removeItem(CHUNK_LOAD_ERROR_KEY);
});

window.addEventListener('error', (event) => {
  const isChunkLoadError =
    event.message?.includes('Failed to fetch dynamically imported module') ||
    event.message?.includes('Importing a module script failed') ||
    event.error?.name === 'ChunkLoadError';

  if (isChunkLoadError) {
    handleChunkLoadError(event.message);
  }
});

window.addEventListener('unhandledrejection', (event) => {
  const isChunkLoadError =
    event.reason?.message?.includes('Failed to fetch dynamically imported module') ||
    event.reason?.message?.includes('Importing a module script failed');

  if (isChunkLoadError) {
    event.preventDefault(); // Prevent the default error handling
    handleChunkLoadError(event.reason?.message);
  }
});

root.render(
  <StrictMode>
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Suspense fallback={<Loading show={true} />}>
        <I18nextProvider i18n={i18n}>
          <QueryClientProvider client={queryClient}>
            <ThemeProvider>
              <ReactQueryDevtools initialIsOpen={false} />
              <App />
            </ThemeProvider>
          </QueryClientProvider>
        </I18nextProvider>
      </Suspense>
    </ErrorBoundary>
  </StrictMode>
);
